import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { InterviewContextZone } from "@/components/build/InterviewContextZone";
import { RatingStars } from "@/components/build/RatingStars";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";
import {
  MapPin, Clock, AlertTriangle, CheckCircle2, XCircle, PauseCircle, Loader2, FolderCheck, Ban, Phone,
  MailWarning, RotateCw, Copy, Check,
} from "lucide-react";

// Ticket 4 (2026-02), rebuilt Part A (2026-02): the Interview page + Onboard action, now split
// into the two zones from NJM's Google-Sheet interview format — a read-only "Context" zone
// (InterviewContextZone, shared with Past Interviews) and this page's own "Interviewer Capture"
// zone: Interviewed by, Interview Date, Attitude Rating [1-5], Attitude Notes, Expected Cost
// per Project, Availability, Additional Notes, Overall Rating [1-5] (UI relabel of the
// pre-existing NJM Rating field), Decision. Part B: Reject sets Onboarding Status=Declined +
// "Do Not Re-Accept"=Yes server-side (see build.py's submit_interview) — this page just shows
// the resulting record-retained/email-sent confirmation.

function QueueRow({ c, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`interview-row-${c.id}`}
      className={`w-full text-left px-4 py-3 border-b border-hairline transition-colors ${active ? "bg-brand/10" : "hover:bg-surface2"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink truncate">{c.name}</div>
          <div className="text-xs text-ink-soft truncate">{c.primary_role || "—"}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0 mt-0.5">
          {c.invite_unconfirmed && (
            <span title="Invite email not confirmed sent" data-testid={`interview-row-invite-badge-${c.id}`}>
              <MailWarning className="h-3.5 w-3.5 text-amber-500" />
            </span>
          )}
          {c.stale && (
            <span title={`Waiting ${c.days_waiting} days`}>
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-faint">
        {c.city && <span className="inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{c.city}</span>}
        <span className="inline-flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{c.days_waiting ?? 0}d waiting</span>
        {c.phone && (
          <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} data-testid={`interview-row-phone-${c.id}`}
            title={`Call ${c.phone}`} className="inline-flex items-center gap-0.5 text-ink-soft hover:text-brand hover:underline">
            <Phone className="h-2.5 w-2.5" />{c.phone}
          </a>
        )}
      </div>
    </button>
  );
}

// Bugfix (2026-09-29, Arush Mathur incident): the Calendly invite send is NEVER assumed
// successful — `invite_email_status` is persisted on the record itself (survives reload) and
// this panel is the one place a PM can see the true state, re-fire the send, or fall back to
// manually sharing the link. `status === undefined` (legacy record, Selected before this fix
// shipped) is deliberately labelled "Unknown" rather than assumed sent.
function InviteEmailStatusPanel({ detail, onResent }) {
  const [resending, setResending] = useState(false);
  const [copied, setCopied] = useState(false);
  const guard = useEmailGuard();
  const status = detail.invite_email_status;

  const resend = async () => {
    setResending(true);
    try {
      const r = await api.post(`/v1/build/interviews/${detail.id}/resend-invite`);
      // Email Send Guard (2026-02): resend-invite is a single external recipient (the
      // applicant's own address) — the ENTIRE action (nothing written yet) waits for a human
      // to confirm via the shared "This will email N people" modal before it actually fires.
      if (guard.intercept(r.data, (typedCount) =>
        api.post(`/v1/build/interviews/${detail.id}/resend-invite`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount })
          .then((cr) => {
            onResent(cr.data);
            if (cr.data.email_sent) toast.success("Invite sent.");
            else toast.warning(`Still not sent — ${cr.data.invite_email_error || cr.data.invite_email_status}.`);
          })
      )) {
        return;
      }
      onResent(r.data);
      if (r.data.email_sent) {
        toast.success("Invite sent.");
      } else {
        toast.warning(`Still not sent — ${r.data.invite_email_error || r.data.invite_email_status}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't resend the invite");
    } finally { setResending(false); }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(detail.calendly_link || "").then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); });
    toast.success("Calendly link copied — share it by WhatsApp or email.");
  };

  if (status === "sent") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="interview-invite-status-sent">
        <CheckCircle2 className="h-3.5 w-3.5" /> Calendly invite sent{detail.invite_email_attempted_at ? ` · ${new Date(detail.invite_email_attempted_at).toLocaleString()}` : ""}
      </div>
    );
  }
  const label = status == null ? "Unknown — please confirm" : status === "blocked" ? "Blocked" : "Failed";
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 space-y-2" data-testid="interview-invite-status-unconfirmed">
      <div className="flex items-center gap-1.5 text-xs font-medium text-amber-900">
        <MailWarning className="h-3.5 w-3.5" /> Invite email not sent — {label}{detail.invite_email_error ? `: ${detail.invite_email_error}` : ""}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={resend} disabled={resending} data-testid="interview-resend-invite-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
          {resending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />} Resend invite
        </button>
        <button onClick={copyLink} data-testid="interview-copy-calendly-link-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900">
          {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />} Copy Calendly link
        </button>
      </div>
      <EmailConfirmModal open={guard.open} preview={guard.preview} busy={guard.busy} onConfirm={guard.confirm} onCancel={guard.cancel} />
    </div>
  );
}

export default function InterviewQueue() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unconfirmedCount, setUnconfirmedCount] = useState(0);
  const [unconfirmedOnly, setUnconfirmedOnly] = useState(false);
  // Header-count fix (2026-02): the backend's own `total` is whatever the current
  // unconfirmed_only filter narrows it to (6 when filtered) — this tracks the TRUE,
  // unfiltered "Selected" total (13) from the last unfiltered fetch (always the initial
  // mount load, since `unconfirmedOnly` starts false) so the header can show "6 of 13" while
  // filtered, instead of silently dropping the "of 13" context.
  const [totalSelected, setTotalSelected] = useState(0);
  const [declineReasons, setDeclineReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [decision, setDecision] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const guard = useEmailGuard();

  const loadList = useCallback((unconfirmedOnlyArg) => {
    setLoading(true);
    return api.get("/v1/build/interviews", { params: unconfirmedOnlyArg ? { unconfirmed_only: true } : {} }).then((r) => {
      setItems(r.data.items || []);
      setUnconfirmedCount(r.data.unconfirmed_count || 0);
      setDeclineReasons(r.data.decline_reasons || []);
      if (!unconfirmedOnlyArg) setTotalSelected(r.data.total || 0);
    }).catch(() => toast.error("Couldn't load the interview queue")).finally(() => setLoading(false));
  }, []);

  const loadDetail = useCallback((id) => {
    setDetailLoading(true);
    api.get(`/v1/build/interviews/${id}`).then((r) => {
      setDetail(r.data);
      setForm({
        interview_date: r.data.interview_date || new Date().toISOString().slice(0, 10),
        interviewer: r.data.interviewer || user?.name || user?.email || "",
        overall_rating: r.data.njm_rating ?? null,
        attitude_rating: r.data.attitude_rating ?? null,
        attitude_notes: r.data.attitude_notes || "",
        expected_cost_per_project: r.data.expected_cost_per_project || "",
        day_rate_quoted: r.data.day_rate_quoted ?? "",
        availability_notes: r.data.availability_notes || "",
        notes: "",
      });
      setDecision(null);
      setDeclineReason("");
    }).catch(() => toast.error("Couldn't load this candidate")).finally(() => setDetailLoading(false));
  }, [user]);

  useEffect(() => { loadList(unconfirmedOnly); }, [loadList, unconfirmedOnly]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); else { setDetail(null); setForm(null); } }, [selectedId, loadDetail]);

  const submit = async () => {
    if (!decision) { toast.error("Choose a decision first"); return; }
    if (!form.interview_date || !form.interviewer.trim()) { toast.error("Interview Date and Interviewer are required"); return; }
    if (decision === "Reject" && !declineReason) { toast.error("Choose a decline reason first"); return; }
    setBusy(true);
    try {
      const payload = {
        interview_date: form.interview_date, interviewer: form.interviewer.trim(),
        rating: form.overall_rating, attitude_rating: form.attitude_rating, attitude_notes: form.attitude_notes,
        expected_cost_per_project: form.expected_cost_per_project || null,
        day_rate_quoted: form.day_rate_quoted === "" ? null : Number(form.day_rate_quoted),
        availability_notes: form.availability_notes || null, notes: form.notes, decision,
      };
      if (decision === "Reject") payload.decline_reason = declineReason;
      const r = await api.post(`/v1/build/interviews/${selectedId}/submit`, payload);
      // Email Send Guard (2026-02): the Onboard/Reject decision write (status, Drive folder,
      // Do Not Re-Accept flag) above ALWAYS happens in this one call — only the welcome/
      // decline EMAIL (always external) waits for a human to confirm via the shared "This
      // will email N people" modal.
      const finishUpQueue = () => {
        setItems((prev) => prev.filter((i) => i.id !== selectedId));
        // Cursor-review fix (2026-02): every decision (Onboard/Hold/Reject) moves the
        // candidate's onboarding_status away from "Selected" server-side, so the TRUE
        // unfiltered total shrinks by exactly 1 here too — without this, "N of total" kept
        // showing the stale pre-decision total until the next full page load.
        setTotalSelected((t) => Math.max(0, t - 1));
        setSelectedId(null);
        window.__njmRefreshBadges?.();
      };
      if (guard.intercept(r.data, (typedCount) =>
        api.post(`/v1/build/admin/emails/confirm-pending`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount })
          .then((cr) => toast.success(cr.data.email_sent ? `${decision === "Onboard" ? "Welcome" : "Decline"} email sent.` : `${decision === "Onboard" ? "Welcome" : "Decline"} email did NOT send.`))
      )) {
        toast.success(decision === "Onboard" ? (r.data.drive_folder_url ? "Onboarded — Drive folder created." : "Onboarded (Drive folder skipped, check Settings).") : "Declined — record retained, flagged Do Not Re-Accept.");
        finishUpQueue();
        return;
      }
      if (decision === "Onboard") {
        const welcomeSent = r.data.email_sent;
        if (welcomeSent === false) {
          toast.warning(r.data.drive_folder_url ? "Onboarded — Drive folder created, but the welcome email did NOT send." : "Onboarded, but the welcome email did NOT send (Drive folder also skipped).");
        } else {
          toast.success(r.data.drive_folder_url ? "Onboarded — Drive folder created, welcome email sent." : "Onboarded — welcome email sent (Drive folder skipped, check Settings).");
        }
      } else if (decision === "Hold") {
        toast.success("Held — status set to Interviewed.");
      } else {
        toast.success(r.data.email_sent === false ? "Declined — record retained, flagged Do Not Re-Accept. Email did NOT send." : "Declined — email sent, record retained, flagged Do Not Re-Accept.");
      }
      // Code-review fix (2026-10, Cursor audit item 10): this decision just moved a row out
      // of Interviews (and possibly onto the approved roster, changing build_pending too) —
      // refresh the sidebar badges immediately instead of the usual 60s wait.
      finishUpQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't submit this interview");
    } finally { setBusy(false); }
  };

  return (
    <>
    <div className="flex h-full min-h-0" data-testid="interview-queue-page">
      <div className="w-[340px] shrink-0 border-r border-hairline flex flex-col min-h-0">
        <div className="px-4 py-3.5 border-b border-hairline space-y-2">
          <h1 className="font-editorial text-lg text-ink">Interviews</h1>
          <p className="text-xs text-faint">{unconfirmedOnly && totalSelected > 0 ? `${items.length} of ${totalSelected} selected, awaiting interview` : `${items.length} selected, awaiting interview`}</p>
          {unconfirmedCount > 0 && (
            <button onClick={() => setUnconfirmedOnly((v) => !v)} data-testid="interview-unconfirmed-filter-toggle"
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors ${unconfirmedOnly ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"}`}>
              <MailWarning className="h-3 w-3" /> {unconfirmedOnly ? "Showing" : "Show"} {unconfirmedCount} invite not confirmed sent
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="interview-queue-list">
          {loading ? (
            <div className="p-6 text-center text-sm text-faint">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyState title={unconfirmedOnly ? "No unconfirmed invites" : "No candidates awaiting interview"} hint="Selected applicants from /admin/applications will show up here." testId="interview-queue-empty" />
          ) : (
            items.map((c) => <QueueRow key={c.id} c={c} active={c.id === selectedId} onClick={() => setSelectedId(c.id)} />)
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {!selectedId ? (
          <EmptyState title="Select a candidate to begin the interview" testId="interview-detail-empty" />
        ) : detailLoading || !detail || !form ? (
          <div className="p-8 text-center text-sm text-faint">Loading…</div>
        ) : (
          <div className="max-w-3xl mx-auto px-6 py-6 space-y-6" data-testid="interview-detail-pane">
            <div className="text-xs text-faint">Selected {detail.selected_on} — {detail.days_waiting} day{detail.days_waiting === 1 ? "" : "s"} waiting</div>

            <InviteEmailStatusPanel
              detail={detail}
              onResent={(data) => {
                setDetail((d) => ({ ...d, invite_email_status: data.invite_email_status, invite_email_error: data.invite_email_error, invite_email_attempted_at: data.invite_email_attempted_at }));
                setItems((prev) => prev.map((i) => (i.id === selectedId ? { ...i, invite_email_status: data.invite_email_status, invite_unconfirmed: data.invite_email_status !== "sent" } : i)));
                if (data.invite_email_status === "sent") setUnconfirmedCount((c) => Math.max(0, c - 1));
              }}
            />

            <InterviewContextZone detail={detail} />

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Bio</div>
              <p className="text-sm text-ink whitespace-pre-wrap" data-testid="interview-bio">{detail.bio || <span className="text-faint italic">Not provided.</span>}</p>
            </div>

            {detail.application_v2_answers && Object.keys(detail.application_v2_answers).length > 0 && (
              <div data-testid="interview-v2-answers">
                <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Application Answers</div>
                <div className="space-y-1.5">
                  {Object.entries(detail.application_v2_answers).map(([k, v]) => (
                    <div key={k} className="text-sm text-ink" data-testid={`interview-v2-answer-${k}`}>
                      <span className="text-faint">{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}:</span>{" "}
                      {Array.isArray(v) ? v.join(", ") : v}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.internal_notes && (
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Internal Notes (from Applications review)</div>
                <div className="space-y-2">
                  {detail.internal_notes.split("\n\n").filter(Boolean).map((note, i) => (
                    <div key={i} className="text-xs text-ink-soft bg-surface2 rounded-md px-3 py-2 whitespace-pre-wrap">{note}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-md border border-hairline p-4 space-y-4" data-testid="interview-form">
              <div className="text-xs font-medium uppercase tracking-wider text-faint">Interviewer Capture</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-faint">Interview Date</span>
                  <input type="date" data-testid="interview-date-input" value={form.interview_date}
                    onChange={(e) => setForm({ ...form, interview_date: e.target.value })}
                    className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-faint">Interviewed By</span>
                  <input type="text" data-testid="interview-interviewer-input" value={form.interviewer}
                    onChange={(e) => setForm({ ...form, interviewer: e.target.value })}
                    className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-faint">Attitude Rating</span>
                  <RatingStars testIdPrefix="interview-attitude-rating" value={form.attitude_rating} onChange={(v) => setForm({ ...form, attitude_rating: v })} />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-faint">Overall Rating</span>
                  <RatingStars testIdPrefix="interview-overall-rating" value={form.overall_rating} onChange={(v) => setForm({ ...form, overall_rating: v })} />
                </div>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-faint">Attitude Notes</span>
                <textarea rows={2} data-testid="interview-attitude-notes-input" value={form.attitude_notes}
                  onChange={(e) => setForm({ ...form, attitude_notes: e.target.value })} placeholder="Communication, professionalism, culture fit…"
                  className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-faint">Expected Cost per Project</span>
                  <input type="text" data-testid="interview-expected-cost-input" value={form.expected_cost_per_project}
                    onChange={(e) => setForm({ ...form, expected_cost_per_project: e.target.value })} placeholder="e.g. ₹15k/day or ₹50k/project"
                    className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-faint">Day Rate (optional, ₹/day)</span>
                  <input type="number" data-testid="interview-day-rate-input" value={form.day_rate_quoted}
                    onChange={(e) => setForm({ ...form, day_rate_quoted: e.target.value })} placeholder="₹"
                    className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-faint">Availability</span>
                <input type="text" data-testid="interview-availability-notes-input" value={form.availability_notes}
                  onChange={(e) => setForm({ ...form, availability_notes: e.target.value })} placeholder="e.g. Available weekdays, needs 1 week notice"
                  className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-faint">Additional Notes</span>
                <textarea rows={3} data-testid="interview-notes-input" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Interview notes…"
                  className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
              </label>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-hairline sticky bottom-0 bg-paper py-3 flex-wrap">
              <button data-testid="interview-decision-onboard-btn" onClick={() => setDecision("Onboard")} disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50 ${decision === "Onboard" ? "bg-brand text-ink" : "border border-hairline text-ink-soft hover:bg-surface2"}`}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Onboard
              </button>
              <button data-testid="interview-decision-hold-btn" onClick={() => setDecision("Hold")} disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 ${decision === "Hold" ? "bg-ink text-white" : "border border-hairline text-ink-soft hover:bg-surface2"}`}>
                <PauseCircle className="h-3.5 w-3.5" /> Hold
              </button>
              <button data-testid="interview-decision-reject-btn" onClick={() => setDecision("Reject")} disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium border disabled:opacity-50 ${decision === "Reject" ? "bg-red-600 text-white border-red-600" : "border-red-300 text-red-700 hover:bg-red-50"}`}>
                <XCircle className="h-3.5 w-3.5" /> Reject
              </button>
            </div>

            {decision === "Reject" && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 space-y-3" data-testid="interview-decline-panel">
                <div className="text-xs font-medium text-red-900">Decline reason (required)</div>
                <select data-testid="interview-decline-reason-select" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                  className="w-full rounded-md border border-red-300 bg-white px-3 py-2 text-sm text-ink">
                  <option value="">Select a reason…</option>
                  {declineReasons.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <div className="flex items-start gap-2 text-[11px] text-red-800">
                  <Ban className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>This will set Onboarding Status = Declined and flag "Do Not Re-Accept" — this email will be permanently blocked from re-applying. The record itself is retained, never deleted.</span>
                </div>
              </div>
            )}

            {decision === "Onboard" && (
              <div className="rounded-md border border-brand/40 bg-brand/10 px-4 py-3 text-xs text-ink flex items-start gap-2" data-testid="interview-onboard-preview">
                <FolderCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>Onboarding will publish this profile, create a Drive subfolder for their role, and send the branded Welcome email (NDA, rate card, WhatsApp group).</span>
              </div>
            )}

            {decision && (
              <button data-testid="interview-submit-btn" onClick={submit} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Confirm {decision}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    <EmailConfirmModal
      open={guard.open}
      preview={guard.preview}
      busy={guard.busy}
      onConfirm={guard.confirm}
      onCancel={guard.cancel}
    />
    </>
  );
}
